import { useMemo } from 'react';

import type { Driver } from '#domain/Driver.ts';
import type { Tour } from '#domain/Tour.ts';
import type { Vehicle } from '#domain/Vehicle.ts';
import { filterStopInSearch } from '#utils/searchUtils.ts';

const containsSearchTerm = <T extends Tour>({
    tour,
    vehicles,
    drivers,
    search,
}: {
    tour: T;
    vehicles: Vehicle[];
    drivers?: Driver[];
    search: string;
}) => {
    const text = search.trim().toLowerCase();
    return (
        vehicles
            ?.find(vehicle => vehicle?.id === tour?.truck.id)
            ?.plate?.toLowerCase()
            .includes(text) ||
        drivers
            ?.find(driver => driver?.id === tour?.driverId)
            ?.name?.toLowerCase()
            .includes(text) ||
        tour.id?.toLowerCase().includes(text) ||
        tour.truck.id?.toLowerCase().includes(text) ||
        tour.searchName?.toLowerCase().includes(text) ||
        filterStopInSearch(text, tour.firstStop) ||
        filterStopInSearch(text, tour.lastStop)
    );
};

const containsFilterTerm = (item: Tour, statusFilter: string[]) =>
    statusFilter.length === 0 || statusFilter.includes(item.status);

export const useFilteredTours = <T extends Tour>({
    tours,
    vehicles,
    drivers,
    search,
    statusFilter,
    selectedTourId,
}: {
    tours: T[];
    vehicles: Vehicle[];
    drivers?: Driver[];
    search: string;
    statusFilter: string[];
    selectedTourId?: string;
}) => {
    const filteredTours = useMemo(
        () =>
            tours
                .filter(tour => containsSearchTerm({ tour, vehicles, drivers, search }))
                .filter(tour => containsFilterTerm(tour, statusFilter)),
        [drivers, search, statusFilter, tours, vehicles]
    );
    const activeTour = useMemo(() => tours.find(item => item.id === selectedTourId), [selectedTourId, tours]);

    return {
        filteredTours,
        activeTour,
    };
};
